/**
 * Phase 3.6: 比較ビュー定型文データ（具体例重視版 + グループ間遷移調整版）
 *
 * Phase 3.5 をベースに、グループ間遷移文12パターンの問いかけフレーズを削除。
 * グループ間遷移文 + 軸別文の二段表示時に「最近、こんな〜?」が2回出る冗長を解消。
 *
 * 構造（軸別文）:
 *   詩的見出し1行
 *   → 「最近、こんな〜ありませんか?」
 *   → 具体例 3〜5個
 *   → 締めの抽象解釈
 *
 * 構造（グループ間遷移文）:
 *   詩的見出し1行
 *   → 抽象的な比喩説明
 *   → 「たとえば、」で導入する具体例 4〜6個
 *   → 締めの抽象解釈
 *
 * 48パターンの内訳:
 * - 軸別変化文 32パターン（4軸 × 8）
 * - グループ間遷移 12パターン
 * - 同日比較 2パターン
 * - 微小変化 2パターン
 */

import type { MbtiType } from '@/features/diagnosis/logic/types';

// =============================================================
// 型定義
// =============================================================

export type Axis = 'EI' | 'SN' | 'TF' | 'JP';

export type AxisStrength = 'none' | 'small' | 'medium' | 'flip';

export type Direction =
  | 'left_to_right'    // I→E, S→N, T→F, J→P
  | 'right_to_left'    // E→I, N→S, F→T, P→J
  | 'stable_left'      // I, S, T, J で変化なし
  | 'stable_right';    // E, N, F, P で変化なし

export type Group = 'analysts' | 'diplomats' | 'sentinels' | 'explorers';

export interface AxisInterpretation {
  axis: Axis;
  direction: Direction;
  strength: AxisStrength;
  text: string;
}

export interface GroupTransitionInterpretation {
  from: Group;
  to: Group;
  text: string;
}

export interface SameDayInterpretation {
  typeChanged: boolean;
  text: string;
}

export interface MinimalChangeInterpretation {
  variant: 'subtle_movement' | 'all_stable';
  text: string;
}

// =============================================================
// タイプ → グループのマッピング
// =============================================================

export const TYPE_TO_GROUP: Record<MbtiType, Group> = {
  INTJ: 'analysts', INTP: 'analysts', ENTJ: 'analysts', ENTP: 'analysts',
  INFJ: 'diplomats', INFP: 'diplomats', ENFJ: 'diplomats', ENFP: 'diplomats',
  ISTJ: 'sentinels', ISFJ: 'sentinels', ESTJ: 'sentinels', ESFJ: 'sentinels',
  ISTP: 'explorers', ISFP: 'explorers', ESTP: 'explorers', ESFP: 'explorers',
};

// =============================================================
// 軸別変化文 32パターン
// =============================================================

export const AXIS_INTERPRETATIONS: AxisInterpretation[] = [
  // ---- EI 軸 ----
  {
    axis: 'EI', direction: 'left_to_right', strength: 'small',
    text: `内向的だったあなたの中に、
ほんの少し、外への動きが生まれている。

最近、こんな瞬間はありませんか?

ひとりの時間は今も大切だけれど、
誰かと話したあとの満足感が、
前より素直に残るようになった。
苦手だと思っていた集まりに、
意外と最後まで居られた。

大きな変化ではないけれど、
あなたの中の何かが、わずかに
外の世界の方を向きはじめています。`,
  },
  {
    axis: 'EI', direction: 'left_to_right', strength: 'medium',
    text: `内向的だったあなたが、少しずつ心をひらいていく。

最近、こんな変化はありませんか?

ひとりで本を読む時間より、
誰かと話している時間のほうが、
最近は心地よく感じる。
職場の雑談に、以前より自然に
入っていけるようになった。
休日に人と会う予定を、
進んで入れるようになっている。

ひとりの時間を大切にしてきたあなたが、
誰かと過ごす時間にも、
じわりと馴染んでいるようです。`,
  },
  {
    axis: 'EI', direction: 'left_to_right', strength: 'flip',
    text: `内向的だったあなたが、外へと開いていく。

最近、こんな変化はありませんか?

ひとりでカフェに行くより、
友達と過ごす時間のほうが心地よく感じる。
休日の予定を、進んで埋めようとしている。
新しい場所に誘われると、前なら断っていたのに、
今は行きたいと思える。

そんな小さな変化の連なりは、
あなたのエネルギーの向きが、
内から外へと変わりはじめているサインかもしれません。`,
  },
  {
    axis: 'EI', direction: 'right_to_left', strength: 'small',
    text: `外向的なあなたの中に、
静けさを求める気持ちが芽生えている。

最近、こんな感覚はありませんか?

人と過ごす時間は変わらず好きだけれど、
帰り道に少し疲れを感じる。
予定が詰まっている週末より、
何もない一日のほうが心待ちになる。
誰にも会わずに過ごす時間が、
小さなご褒美のように思える。

ひとりでいる時間に、
あなたなりの意味が
生まれはじめているのかもしれません。`,
  },
  {
    axis: 'EI', direction: 'right_to_left', strength: 'medium',
    text: `外向的だったあなたが、少しずつ内へと向かう。

最近、こんな変化はありませんか?

大人数の飲み会に行く前に、
以前より少し気が重くなる。
家でひとり過ごす夜が、
不思議と心地よく感じる。
SNSを開く時間より、
本やドラマに向き合う時間が増えている。

人の中にいる時間も大切にしながら、
自分の内側に耳を澄ませる時間が、
あなたの中で意味を増しているようです。`,
  },
  {
    axis: 'EI', direction: 'right_to_left', strength: 'flip',
    text: `外へ開いていたあなたが、内へと向かう。

最近、こんな変化はありませんか?

賑やかな場所が、前ほど楽しいと感じない。
ひとりで考えごとをする時間が、
何より大切に思える。
親しい人との一対一の会話のほうが、
大人数の集まりよりずっと豊かに感じる。
誰かと一緒にいる時間より、
ひとりでいる時間のほうが自然に流れる。

人の中にいた重心が、
あなた自身の内側へと深く沈んでいるようです。`,
  },
  {
    axis: 'EI', direction: 'stable_left', strength: 'none',
    text: `あなたは引き続き、内向的なあなたのまま。

最近も、こんな時間を大切にしていませんか?

賑やかな場所より、静かなカフェ。
大人数の集まりより、気の合う人との一対一。
休日にひとりで過ごす時間が、
あなたを満たしている。

細かな波はありながらも、
自分の世界を深めていく姿勢は
変わらず保たれています。

揺らぎながらも保たれている軸。
それはあなたの核のひとつです。`,
  },
  {
    axis: 'EI', direction: 'stable_right', strength: 'none',
    text: `あなたは引き続き、外向的なあなたのまま。

最近も、こんな時間を大切にしていませんか?

人と会う約束が入ると、それだけで気分が上がる。
誰かと話していると、自分のアイデアがどんどん広がる。
ひとりの時間より、誰かといる時間のほうが
自然にエネルギーが湧いてくる。

細かな波はあっても、
心が外へ向かう力は変わっていません。

揺らぎながらも保たれている軸。
それはあなたの核のひとつです。`,
  },

  // ---- SN 軸 ----
  {
    axis: 'SN', direction: 'left_to_right', strength: 'small',
    text: `現実を見つめるあなたの中に、
直感が顔を出しはじめている。

最近、こんな瞬間はありませんか?

事実を確かめてから動く習慣は変わらないけれど、
ふと「なんとなくこっち」という感覚に
従ってみたくなる。
データや評価より、
自分の中に浮かぶイメージを
ちょっとだけ信じてみる。

小さな動きですが、
目の前の事実の奥にある何かに、
あなたの感覚が伸びはじめています。`,
  },
  {
    axis: 'SN', direction: 'left_to_right', strength: 'medium',
    text: `現実的だったあなたが、少しずつ直感に耳を傾けはじめる。

最近、こんな変化はありませんか?

予定通りに進めるより、
その時の気分や直感で選びたくなる。
事実を確かめる前に、
「これ、いい予感がする」と思える瞬間が増える。
人と会った時、
その人の言葉より雰囲気が気になる。

足元の確かさを大切にしながらも、
言葉にならない予感を、
あなたは信じはじめているようです。`,
  },
  {
    axis: 'SN', direction: 'left_to_right', strength: 'flip',
    text: `現実を見つめていたあなたが、直感の世界へと開かれていく。

最近、こんな感覚はありませんか?

事実より、その奥にあるものを見たくなる。
論理的な説明より、
直感で感じたことのほうが正しい気がする。
将来の可能性について考える時間が、
今を整理する時間より長くなった。
「なんとなくそう感じる」という感覚が、
あなたを動かす最大の理由になっている。

確かな手触りを頼りに歩んできた日々から、
まだ見えないものを信じる日々へ、
あなたの視線は遠くへと伸びています。`,
  },
  {
    axis: 'SN', direction: 'right_to_left', strength: 'small',
    text: `直感的なあなたの中に、
現実を確かめる視点が育っている。

最近、こんな瞬間はありませんか?

ひらめきを大切にする姿勢は変わらないけれど、
それを実行する前に、一度立ち止まって
事実を確かめたくなる。
SNSで見た情報を、
ソースを調べてから受け取る。
夢のような計画より、
今週やれることに目を向ける。

ふわふわした感覚はそのままに、
足元を一歩確かめる視点が、
あなたの中に芽生えています。`,
  },
  {
    axis: 'SN', direction: 'right_to_left', strength: 'medium',
    text: `直感的だったあなたが、少しずつ現実に足をつける。

最近、こんな変化はありませんか?

「いつか」と思っていたことより、
「今日」できることが気になる。
頭の中の世界に浸る時間より、
目の前のものを丁寧に扱う時間が増えた。
雲の上を歩くような会話より、
具体的な話のほうが心地よく感じる。

遠くを見つめる時間を大切にしながらも、
今この瞬間の手触りに、
あなたの意識が向かいはじめています。`,
  },
  {
    axis: 'SN', direction: 'right_to_left', strength: 'flip',
    text: `直感を信じていたあなたが、現実へと深く根を下ろす。

最近、こんな感覚はありませんか?

可能性の話より、確実な話を信じたくなる。
夢を語る友人より、
着実に何かを積み重ねている友人に共感する。
「いつか」より「今日」、
「かもしれない」より「ある」を選ぶようになった。
頭の中で世界を膨らませるより、
目の前の一杯のコーヒーを味わうほうが豊かに感じる。

ひらめきと予感の中を漂っていた時期から、
目の前の確かなものに向き合う時期へ、
あなたの重心は深く沈んでいるようです。`,
  },
  {
    axis: 'SN', direction: 'stable_left', strength: 'none',
    text: `あなたは引き続き、現実的なあなたのまま。

最近も、こんな姿勢を大切にしていませんか?

新しい情報には、まず根拠を確かめる。
具体的な行動を、
一歩ずつ積み重ねていく。
派手な夢より、
今日できることに集中する。

細かな波はありながらも、
足元を確かめる姿勢は
変わらず保たれています。

揺らぎながらも保たれている視点。
それはあなたの核のひとつです。`,
  },
  {
    axis: 'SN', direction: 'stable_right', strength: 'none',
    text: `あなたは引き続き、直感的なあなたのまま。

最近も、こんな感覚を大切にしていませんか?

事実より、その奥にあるものを見ようとする。
今日のことより、未来の可能性に心が動く。
「なんとなくこっち」という感覚が、
あなたを正しい方向へ導いている。

世界の奥にあるものを見ようとする力は、
変わらず保たれています。

揺らぎながらも保たれている視点。
それはあなたの核のひとつです。`,
  },

  // ---- TF 軸 ----
  {
    axis: 'TF', direction: 'left_to_right', strength: 'small',
    text: `論理を頼りにしてきたあなたの中に、
感性の声が聞こえはじめている。

最近、こんな瞬間はありませんか?

職場で正論を言いかけて、
相手の表情を見て言葉を選び直す。
友達の話を聞きながら、
「正しさ」より「気持ち」を優先したくなる。
誰かを思って何かを選ぶ時間が、
前より自然に流れている。

判断の中に、ほんの少しずつ
感性の余白が生まれてきています。`,
  },
  {
    axis: 'TF', direction: 'left_to_right', strength: 'medium',
    text: `論理的だったあなたが、少しずつ感性に耳を傾けはじめる。

最近、こんな変化はありませんか?

意見が違う相手と話す時、
反論より共感が先に出てくる。
仕事で決断する前に、
チームの気持ちを確かめたくなる。
恋愛で「合うかどうか」より、
「一緒にいて心地いいか」を大切にする。

理屈で物事を決めてきたあなたが、
気持ちの動きを、
判断の中に置きはじめているようです。`,
  },
  {
    axis: 'TF', direction: 'left_to_right', strength: 'flip',
    text: `理屈で決めていたあなたが、感性に任せる世界へと移っていく。

最近、こんな感覚はありませんか?

論理的に正しいかより、
自分の心が動くかが大事になった。
仕事の選択を、
データより直感で決めるようになった。
人との関係で、
「効率」より「温度」を求めるようになった。
誰かの話を聞く時、
「分析する」より「感じる」が自然になっている。

論理の筋道を頼りに歩んできた日々から、
気持ちの響きを信じる日々へ、
あなたの判断の重心は大きく動いています。`,
  },
  {
    axis: 'TF', direction: 'right_to_left', strength: 'small',
    text: `感性に任せてきたあなたの中に、
理屈で確かめる視点が育っている。

最近、こんな瞬間はありませんか?

気持ちで動きたい時にも、
一度立ち止まって理由を考える。
友達の悩み相談に、
共感だけでなく具体的な提案を返す。
買い物の前に、
感覚だけでなくレビューを確認する。

気持ちの響きを大切にしながらも、
筋道を立てて考える視点が、
あなたの中に少しずつ育っています。`,
  },
  {
    axis: 'TF', direction: 'right_to_left', strength: 'medium',
    text: `感性に任せてきたあなたが、少しずつ論理に重心を移す。

最近、こんな変化はありませんか?

気持ちで動いたあと、
「これでよかったのか」と振り返る癖がついた。
人間関係で揉めた時に、
感情より状況を整理しようとする。
仕事で「やってあげたい」気持ちより、
「やるべきこと」を優先するようになった。

心の響きを大切にしながらも、
冷静に物事を見つめる視線が、
あなたの中で力を増しているようです。`,
  },
  {
    axis: 'TF', direction: 'right_to_left', strength: 'flip',
    text: `感性に任せていたあなたが、論理の世界へと深く入っていく。

最近、こんな感覚はありませんか?

感情で動いていた頃の自分が、
少し遠く感じる。
気持ちより、状況や事実を整理することが
判断の中心になっている。
人と関わる時、
「優しさ」より「正確さ」を選ぶ瞬間が増えた。
直感で決めるより、
理由を言葉にできる選択を信じたくなる。

気持ちの流れに沿って動いていた時期から、
理屈で物事を整える時期へ、
あなたの判断の軸は大きく動いています。`,
  },
  {
    axis: 'TF', direction: 'stable_left', strength: 'none',
    text: `あなたは引き続き、論理的なあなたのまま。

最近も、こんな姿勢を大切にしていませんか?

感情に流されず、状況を整理する。
判断の前に、根拠を確かめる。
人と話す時も、論点を明確に保つ。

細かな波はありながらも、
筋道を立てて物事を見つめる姿勢は
変わらず保たれています。

揺らぎながらも保たれている重心。
それはあなたの核のひとつです。`,
  },
  {
    axis: 'TF', direction: 'stable_right', strength: 'none',
    text: `あなたは引き続き、感性に任せるあなたのまま。

最近も、こんな感覚を大切にしていませんか?

人の気持ちを察することが、自然にできる。
判断の中心に、心の響きがある。
誰かのために動くことが、
あなたを満たしている。

人と人のあいだに生まれるものを
丁寧に感じ取る力は、
変わらず保たれています。

揺らぎながらも保たれている響き。
それはあなたの核のひとつです。`,
  },

  // ---- JP 軸 ----
  {
    axis: 'JP', direction: 'left_to_right', strength: 'small',
    text: `計画を立ててきたあなたの中に、
柔らかさが芽生えはじめている。

最近、こんな瞬間はありませんか?

予定通りに進めたい気持ちは変わらないけれど、
急な誘いを受けてみる。
スケジュールから外れた時間を、
意外と楽しめる。
完璧に準備しなくても、
動き出してみることに抵抗が減った。

決めた道筋を大切にしながらも、
予定にない流れを受け入れる余地が、
あなたの中に生まれています。`,
  },
  {
    axis: 'JP', direction: 'left_to_right', strength: 'medium',
    text: `計画的だったあなたが、少しずつ流れに身を任せはじめる。

最近、こんな変化はありませんか?

きっちり予定を組んでいた休日に、
何もしない時間を作るようになった。
仕事で、
完璧な準備より、走りながら考えることを選ぶ。
人との約束で、
細かい時間より、その日の流れを大切にしたくなる。

道筋を立てて進む安心感も大切にしながら、
その場で起きることに合わせて動く軽やかさが、
あなたの中で育っているようです。`,
  },
  {
    axis: 'JP', direction: 'left_to_right', strength: 'flip',
    text: `道筋を立ててきたあなたが、臨機応変な世界へと開かれていく。

最近、こんな感覚はありませんか?

予定を立てる前に、
「とりあえずやってみる」を選ぶようになった。
完璧な計画より、
その時々の判断のほうが自分らしいと感じる。
休日に何も決めずに出かけることが、
楽しみになっている。
仕事も人間関係も、
固めるより、流れに任せたほうがうまくいく気がする。

決められた予定通りに歩んできた日々から、
その瞬間の流れに合わせて動く日々へ、
あなたの重心は大きく動いています。`,
  },
  {
    axis: 'JP', direction: 'right_to_left', strength: 'small',
    text: `臨機応変なあなたの中に、
道筋を整える視点が育っている。

最近、こんな瞬間はありませんか?

その場の流れで動く軽やかさは変わらないけれど、
週の予定をなんとなく書き出してみる。
仕事で、ToDoリストを作る時間が
少し増えた。
予定通りに動けた日に、
小さな満足感を覚える。

流れに身を任せる軽やかさを保ちながらも、
予定を立てて動く時間が、
あなたの中で意味を増しているようです。`,
  },
  {
    axis: 'JP', direction: 'right_to_left', strength: 'medium',
    text: `臨機応変だったあなたが、少しずつ計画に重心を移す。

最近、こんな変化はありませんか?

「今日何しよう」と朝決めていたのが、
前夜のうちに考えるようになった。
仕事で、即興より準備を選ぶことが増えた。
人との予定も、
ふんわり決めるより、はっきり時間を切るほうが落ち着く。

その場の流れを大切にしながらも、
あらかじめ道筋を立てる落ち着きが、
あなたの中で力を増しているようです。`,
  },
  {
    axis: 'JP', direction: 'right_to_left', strength: 'flip',
    text: `臨機応変だったあなたが、道筋を立てる世界へと深く入っていく。

最近、こんな感覚はありませんか?

予定を決めずに過ごす日が、
前ほど心地よくなくなった。
仕事で、計画と段取りを大切にする自分が、
自然になっている。
人との約束も、ふわっと決めるより、
明確に時間と場所を決めたい。
「流れに任せる」より、
「自分で道を引く」ことが性に合うと感じる。

その瞬間の流れに乗っていた時期から、
予定と計画で物事を整える時期へ、
あなたの重心は大きく動いています。`,
  },
  {
    axis: 'JP', direction: 'stable_left', strength: 'none',
    text: `あなたは引き続き、計画的なあなたのまま。

最近も、こんな姿勢を大切にしていませんか?

朝のうちに、その日の予定を整える。
締切から逆算して、段取りを組む。
人との約束も、
時間と場所を明確に決めたい。

細かな波はありながらも、
道筋を立てて進む姿勢は
変わらず保たれています。

揺らぎながらも保たれている道。
それはあなたの核のひとつです。`,
  },
  {
    axis: 'JP', direction: 'stable_right', strength: 'none',
    text: `あなたは引き続き、臨機応変なあなたのまま。

最近も、こんな感覚を大切にしていませんか?

予定に縛られず、その時の気分で動く。
急な誘いに、ふらっと乗れる。
完璧な計画より、走りながら考えることが
あなたらしい。

その時々の流れを読み、
柔らかく動く軽やかさは、
変わらず保たれています。

揺らぎながらも保たれている軽やかさ。
それはあなたの核のひとつです。`,
  },
];

// =============================================================
// グループ間遷移文 12パターン
// =============================================================

export const GROUP_TRANSITION_INTERPRETATIONS: GroupTransitionInterpretation[] = [
  // ---- Analysts → others ----
  {
    from: 'analysts', to: 'diplomats',
    text: `光の探究者から、光を編む人へ。

論理で世界を解き明かそうとしていたあなたが、
人と人のあいだに流れるものに、
心を向けはじめているようです。

たとえば、「正しいかどうか」より、
「どう感じたか」を聞きたくなる。
仕事で、データより人の表情を読むようになった。
友達の話に、解決策ではなく共感を返したくなる。
誰かの想いに、自分の時間を使うことが
苦でなくなった。

「正しさ」を追っていた視線が、
「美しさ」や「人の想い」へと
ゆっくり移っていきます。`,
  },
  {
    from: 'analysts', to: 'sentinels',
    text: `光の探究者から、光の番人へ。

未来を描いていたあなたが、
今、目の前にある秩序や責任に、
重心を移しはじめているようです。

たとえば、新しい仕組みを考えるより、
今あるものを丁寧に守りたくなる。
仕事で、革新より着実な積み重ねを選ぶ。
日々のルーティンに、
小さな満足感を覚えるようになった。
誰かを支える役割が、
自分に自然に馴染む。

新しい仕組みを構想する力を持ちながらも、
すでにあるものを育てていくことへ、
あなたの誠実さが向かいはじめています。`,
  },
  {
    from: 'analysts', to: 'explorers',
    text: `光の探究者から、光の踊り手へ。

頭の中で組み立てていた構想を、
今、あなたは身体と感覚で
試しはじめているようです。

たとえば、「考えてから動く」より、
「動きながら考える」が増えた。
理屈で組み立てる前に、
直感でやってみることが楽しい。
人と話す時、
分析より反応のほうが先に出る。
完璧な計画より、
即興的な選択のほうが自分らしいと感じる。

考え抜く力を持ちながらも、
その場で感じる軽やかさが、
あなたの中で育っています。`,
  },

  // ---- Diplomats → others ----
  {
    from: 'diplomats', to: 'analysts',
    text: `光を編む人から、光の探究者へ。

人の想いに寄り添ってきたあなたが、
今、少し距離を取って、
物事の構造を見つめはじめているようです。

たとえば、人の話を聞く時、
共感より分析が先に出るようになった。
気持ちで決めていたことを、
理由をつけて整理したくなる。
人間関係で、
近すぎる距離より適度な距離が心地よくなった。
仕事で、人の温度より仕組みを見つめる時間が増えた。

「どう感じるか」を大切にしながらも、
「どう成り立っているか」を冷静に見つめる視線が、
あなたの中に育っています。`,
  },
  {
    from: 'diplomats', to: 'sentinels',
    text: `光を編む人から、光の番人へ。

人の理想や可能性を見つめてきたあなたが、
今、地に足のついた現実を
大切にしはじめているようです。

たとえば、夢のような可能性より、
今日できることに価値を感じる。
理想を語る時間より、
日々を整える時間が長くなった。
人の心の動きに寄り添いながらも、
具体的な行動で示すようになった。
仕事も人間関係も、
「いつか」より「今」を選ぶ。

夢を語ることから、
日々を整えること、誰かを支えることへと、
あなたの重心が移っているようです。`,
  },
  {
    from: 'diplomats', to: 'explorers',
    text: `光を編む人から、光の踊り手へ。

繊細な感性で世界の美しさを集めていたあなたが、
今、その美しさを場に放つ側へと
立ち位置を変えはじめているようです。

たとえば、ひとりで感じていたことを、
誰かと共有したくなる。
ノートに書いていた言葉が、
人前で話す言葉になりはじめた。
内側に貯めていた感性が、
誰かに届く形を求めている。
人前に立つことが、
前より自然に感じる。

ひとり静かに紡いでいた詩が、
誰かと共有する歌になり、
踊りになっていきます。`,
  },

  // ---- Sentinels → others ----
  {
    from: 'sentinels', to: 'analysts',
    text: `光の番人から、光の探究者へ。

秩序を守ってきたあなたが、
今、その秩序そのものを問い直し、
新しい構造を描こうとしているようです。

たとえば、「こうあるべき」より、
「なぜそうなのか」を考えるようになった。
ルールに従うより、
ルールの意味を確かめたくなる。
仕事で、慣例より新しい視点を求める。
今までの判断を、
一度疑ってみる時間が増えた。

受け継いできたものを大切にしながらも、
問い直し、考え抜く視点が、
あなたの中で力を増しています。`,
  },
  {
    from: 'sentinels', to: 'diplomats',
    text: `光の番人から、光を編む人へ。

役割と責任を全うしてきたあなたが、
今、その向こうにある
人の心の動きに、
目を向けはじめているようです。

たとえば、仕事のタスクをこなす中で、
チームメンバーの気持ちが気になる。
ルールを守ることより、
その背景にある思いを大切にしたくなる。
家族や友人との時間に、
前より深く心を寄せる。
誰かの想いに、
自分の時間を使うことが増えた。

整えること、守ることを大切にしながらも、
誰かの想いに寄り添う時間が、
あなたの中で意味を増しています。`,
  },
  {
    from: 'sentinels', to: 'explorers',
    text: `光の番人から、光の踊り手へ。

秩序を守ってきたあなたが、
今、決められた枠の外へと
足を踏み出しはじめているようです。

たとえば、毎日のルーティンを、少し崩したくなる。
完璧な準備より、
その場のノリで動くことが楽しい。
予定通りに進めるより、
予定外の出来事に心が動く。
人との関係も、
役割より気分で動くことが増えた。

予定通りに進めることから、
その瞬間の感覚に従うことへ、
あなたの重心が動いています。

少し肩の力が抜けた、
新しいあなたが顔を出しています。`,
  },

  // ---- Explorers → others ----
  {
    from: 'explorers', to: 'analysts',
    text: `光の踊り手から、光の探究者へ。

その場の感覚で動いてきたあなたが、
今、立ち止まって考え、
構造を読み解こうとしているようです。

たとえば、「とりあえずやってみる」より、
一度考えてから動きたくなる。
直感で選んだことを、
あとで理由をつけて整理したい。
身体で覚えていたことを、
言葉にしようとする時間が増えた。
仕事でも趣味でも、
仕組みや構造を理解したくなる。

身体で覚えていたことを、
言葉と論理で整理しなおす時間が、
あなたの中で始まっています。`,
  },
  {
    from: 'explorers', to: 'diplomats',
    text: `光の踊り手から、光を編む人へ。

今この瞬間を生きてきたあなたが、
今、誰かの心の動きや、
人と人のあいだに流れるものに、
深く意識を向けはじめているようです。

たとえば、自分の楽しみより、
誰かの気持ちに心が向く。
人の話を聞いて、
深く考え込む時間が増えた。
表面的な楽しさより、
意味のあるつながりを求める。
恋愛でも友情でも、
「楽しい」より「分かり合える」を大切にしたい。

軽やかな感性はそのままに、
その感性を誰かのために
使おうとしているのかもしれません。`,
  },
  {
    from: 'explorers', to: 'sentinels',
    text: `光の踊り手から、光の番人へ。

その場の流れに乗ってきたあなたが、
今、自分の足元を整え、
誰かを支える側へと
立ち位置を変えはじめているようです。

たとえば、その時々で動いていたのが、
予定を立てる習慣がつきはじめた。
楽しさを追うより、
責任を果たすことに満足を覚える。
誰かを守りたい、支えたいという気持ちが、
前より強くなった。
継続的な努力に、価値を感じるようになった。

軽やかに動くことから、
確かに守ること、継続することへと、
あなたの重心が移っているようです。`,
  },
];

// =============================================================
// 同日比較 2パターン
// =============================================================

export const SAME_DAY_INTERPRETATIONS: SameDayInterpretation[] = [
  {
    typeChanged: true,
    text: `同じ日の中で、すでに2つのあなたが現れています。
朝と夜で、気分や視点が違うように、
あなたの中には複数の側面が
共存しているのかもしれません。`,
  },
  {
    typeChanged: false,
    text: `同じ日に2度、あなたは同じ場所に立ち戻りました。
今日のあなたは、
ぶれずに自分の中心にいるようです。`,
  },
];

// =============================================================
// 微小変化 2パターン
// =============================================================

export const MINIMAL_CHANGE_INTERPRETATIONS: MinimalChangeInterpretation[] = [
  {
    variant: 'subtle_movement',
    text: `あなたの中で、何かが動こうとしているのかもしれません。

最近、こんな違和感はありませんか?

いつものペースで過ごしているのに、
ふとした瞬間に「あれ?」と感じる。
普段なら気にしないことが、
少し気になりはじめた。
昨日まで好きだったものに、
わずかな違和感を覚える。

数字としては大きな変化ではないけれど、
あなたの中で、いつものあなたの輪郭が
少し揺らいでいるようです。

その揺らぎは、変化の前触れかもしれませんし、
いつもの自分を保つための
小さな調整なのかもしれません。`,
  },
  {
    variant: 'all_stable',
    text: `あなたの中の4つの軸は、ほとんど変わっていません。

最近も、こんな自分でいませんか?

毎朝、ほぼ同じリズムで一日が始まる。
好きなものも、心地よい時間の使い方も、
変わらず手元にある。
人との距離感、
仕事への向き合い方も、
あなたらしさが保たれている。

このReadingは、前回のあなたをそのまま映しています。
それは、あなたの輪郭が
今、安定していることの表れです。

揺らぎながらも、ぶれない核。
それが今日のあなたを支えています。`,
  },
];

// =============================================================
// 強度判定ロジック
// =============================================================

/**
 * 2つのスコアから変化の方向と強度を判定する。
 *
 * 判定優先順位:
 * 1. 変化量の絶対値が 5 以下 → none（揺らぎの範囲）
 * 2. 符号反転（previous と current の符号が逆）→ flip
 * 3. 変化量 51 以上 → flip（軸別文ではlargeとflipを統合）
 * 4. 変化量 21〜50 → medium
 * 5. 変化量 6〜20 → small
 */
export function analyzeAxisChange(
  _axis: Axis,
  previousScore: number,
  currentScore: number,
): { direction: Direction; strength: AxisStrength } {
  const diff = currentScore - previousScore;
  const absDiff = Math.abs(diff);

  // 1. 揺らぎの範囲（変化なし）
  if (absDiff <= 5) {
    // 現在の符号で stable_left / stable_right を決定
    return {
      direction: currentScore < 0 ? 'stable_left' : 'stable_right',
      strength: 'none',
    };
  }

  // 移動方向
  const direction: Direction = diff > 0 ? 'left_to_right' : 'right_to_left';

  // 2. 符号反転（flip）— 両方とも絶対値 6 以上のとき
  const flipped =
    previousScore < 0 && currentScore > 0 ||
    previousScore > 0 && currentScore < 0;
  if (flipped && Math.abs(previousScore) >= 6 && Math.abs(currentScore) >= 6) {
    return { direction, strength: 'flip' };
  }

  // 3. 大きな変化（flipと統合）
  if (absDiff >= 51) {
    return { direction, strength: 'flip' };
  }

  // 4. 中程度
  if (absDiff >= 21) {
    return { direction, strength: 'medium' };
  }

  // 5. 小さな変化
  return { direction, strength: 'small' };
}

/**
 * 軸別文を検索する。該当パターンがなければ null。
 */
export function findAxisInterpretation(
  axis: Axis,
  direction: Direction,
  strength: AxisStrength,
): AxisInterpretation | null {
  return (
    AXIS_INTERPRETATIONS.find(
      (item) =>
        item.axis === axis &&
        item.direction === direction &&
        item.strength === strength,
    ) ?? null
  );
}

/**
 * グループ間遷移文を検索する。
 */
export function findGroupTransition(
  from: Group,
  to: Group,
): GroupTransitionInterpretation | null {
  if (from === to) return null;
  return (
    GROUP_TRANSITION_INTERPRETATIONS.find(
      (item) => item.from === from && item.to === to,
    ) ?? null
  );
}

/**
 * 同日比較文を取得する。
 */
export function getSameDayInterpretation(typeChanged: boolean): SameDayInterpretation {
  return SAME_DAY_INTERPRETATIONS.find((item) => item.typeChanged === typeChanged)!;
}

/**
 * 微小変化文を取得する。
 */
export function getMinimalChangeInterpretation(
  variant: 'subtle_movement' | 'all_stable',
): MinimalChangeInterpretation {
  return MINIMAL_CHANGE_INTERPRETATIONS.find((item) => item.variant === variant)!;
}